import Highcharts from "highcharts/esm/highcharts.src"

import { useMemo } from "react"
import styled from "styled-components"

import { colors, colorScale, spacing, Text } from "@minuthq/meatball-ui-react"
import { getDay } from "date-fns"

import { GraphCard } from "src/components/Homes/DeviceDetails/Overview/GraphCard"
import { useFetchGuestMessagesAggregatedAnalytics } from "src/data/guestAnalytics/guestAnalyticsQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { TTimezone } from "src/data/timezones/timezones"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { BarChart } from "src/ui/Graphs/BarChart"
import { GenericTooltip } from "src/ui/Graphs/GenericTooltip"
import { TBarChartMultiSeriesData } from "src/ui/Graphs/graphTypes"
import { formatDate } from "src/utils/l10n"

export function AiAssistantPerformanceBarChart({
  startDate,
  endDate,
  timezone,
}: {
  startDate: string
  endDate: string
  timezone: TTimezone
}) {
  const { tPlaceholder } = useTranslate()
  const { orgId } = useOrganization()
  const { clock_type } = useGetUser()

  const fetchGuestMessagesAggregatedAnalytics =
    useFetchGuestMessagesAggregatedAnalytics({
      orgId,
      filters: {
        from: startDate,
        to: endDate,
      },
    })

  const isLoading = fetchGuestMessagesAggregatedAnalytics.isLoading
  const isError = fetchGuestMessagesAggregatedAnalytics.isError

  const data = fetchGuestMessagesAggregatedAnalytics.data?.data
  const hasData = (data?.length ?? 0) > 0

  function getGraphAxisOptions():
    | {
        xAxisOptions: Highcharts.XAxisOptions
        yAxisOptions: Highcharts.YAxisOptions
      }
    | undefined {
    const title: Highcharts.YAxisOptions["title"] = {
      text: tPlaceholder("Number of conversations"),
      style: {
        color: colors.textTertiary,
      },
    }

    if (!hasData) {
      return {
        xAxisOptions: {
          min: new Date(startDate).getTime(),
          max: new Date(endDate).getTime(),
          dateTimeLabelFormats: { month: "%b '%y" },
        },
        yAxisOptions: {
          tickInterval: 5,
          tickPositions: [0, 5, 10, 15, 20, 25],
          title,
        },
      }
    }

    const tickPositions = fetchGuestMessagesAggregatedAnalytics.data?.data.map(
      (item) => new Date(item.grouped_by).getTime()
    )

    return {
      xAxisOptions: {
        startOfWeek: getDay(new Date(startDate)),
        tickPositions,
        labels: {
          formatter: function () {
            return Highcharts.dateFormat("%e %b", this.pos)
          },
        },
      },
      yAxisOptions: {
        title,
      },
    }
  }

  const dataSeries: TBarChartMultiSeriesData[] = useMemo(
    () => [
      {
        name: tPlaceholder("Escalated to team"),
        data:
          data?.map((point) => [
            new Date(point.grouped_by).getTime(),
            point.aggregation.escalated_count,
          ]) ?? [],
        fillColor: colors.systemWarningLight,
        legendSymbolColor: colors.systemWarningLight,
        borderColor: colors.systemWarningDark,
      },
      {
        name: tPlaceholder("Resolved by AI Assistant"),
        data:
          data?.map((point) => [
            new Date(point.grouped_by).getTime(),
            point.aggregation.ai_resolved_count,
          ]) ?? [],
        fillColor: colors.systemGoodLight,
        legendSymbolColor: colors.systemGoodLight,
        borderColor: colors.systemGoodDark,
      },
    ],
    [data, tPlaceholder]
  )

  return (
    <GraphCard
      title={tPlaceholder("AI assistant performance over time")}
      titleVariant="heading3"
      isLoading={isLoading}
      isError={isError}
      hideGraphBorder={false}
      hidden={false}
    >
      <BarChart
        dataSeriesType="multi"
        data={dataSeries}
        timezone={timezone.tzCode}
        borderWidth={1}
        borderRadius={2}
        borderColor={colorScale.blue[600]}
        seriesHoverBrightness={0.03}
        stackSeries={true}
        clockType={clock_type}
        showLegend={true}
        height={300}
        interactive={true}
        {...getGraphAxisOptions()}
        tooltip={{
          decimals: 0,
          formatter: ({ date, points }) => {
            return (
              <Tooltip
                date={date}
                timezone={timezone}
                totalMessages={points?.[0]?.total ?? 0}
                escalatedToTeam={points?.[0]?.y ?? 0}
                resolvedByAi={points?.[1]?.y ?? 0}
              />
            )
          },
          unit: "",
        }}
      />
    </GraphCard>
  )
}

function Tooltip({
  date,
  timezone,
  totalMessages,
  escalatedToTeam,
  resolvedByAi,
}: {
  date: Date
  timezone: TTimezone
  totalMessages: number
  escalatedToTeam: number
  resolvedByAi: number
}) {
  const { tPlaceholder } = useTranslate()

  const formattedDate = formatDate({
    date: date.toISOString(),
    timezone: timezone.tzCode,
    excludeTime: true,
  })

  const escalatedToTeamRatio = Math.round(
    (escalatedToTeam / totalMessages) * 100
  )
  const resolvedByAiRatio = Math.round((resolvedByAi / totalMessages) * 100)

  return (
    <GenericTooltip title={formattedDate}>
      <TooltipBox>
        <div>
          <div>
            {escalatedToTeam} - {tPlaceholder("escalated to team")}
          </div>
          <Text color="secondary" variant="bodyS">
            {`${escalatedToTeamRatio}%`}
          </Text>
        </div>

        <div>
          <div>
            {resolvedByAi} - {tPlaceholder("resolved by AI assistant")}
          </div>
          <Text color="secondary" variant="bodyS">
            {`(${resolvedByAiRatio}%)`}
          </Text>
        </div>
      </TooltipBox>
    </GenericTooltip>
  )
}

const TooltipBox = styled.div`
  display: grid;
  gap: ${spacing.XS};
`
